// components/CategoryManager.js
import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';

const CategoryManager = ({ establishmentId, onCategoriesChange }) => {
  const [categories, setCategories] = useState([]);
  const [hierarchicalCategories, setHierarchicalCategories] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    parent_id: null
  });

  const styles = {
    container: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '24px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0
    },
    addButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease-in-out'
    },
    categoryList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    categoryItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      backgroundColor: '#ffffff',
      transition: 'all 0.2s ease-in-out'
    },
    categoryItemChild: {
      marginLeft: '24px',
      borderLeft: '3px solid #e2e8f0',
      paddingLeft: '16px',
      backgroundColor: '#f8fafc'
    },
    categoryInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1
    },
    categoryDetails: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1
    },
    categoryName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0
    },
    categoryDescription: {
      fontSize: '14px',
      color: '#64748b',
      margin: 0
    },
    categoryMeta: {
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '4px'
    },
    categoryActions: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center'
    },
    actionButton: {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    addSubButton: {
      backgroundColor: '#dbeafe',
      color: '#1d4ed8'
    },
    editButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569'
    },
    deleteButton: {
      backgroundColor: '#fef2f2',
      color: '#dc2626'
    },
    expandButton: {
      backgroundColor: 'transparent',
      color: '#6b7280',
      border: '1px solid #e2e8f0',
      width: '24px',
      height: '24px',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      cursor: 'pointer'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '20px'
    },
    inputGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      minHeight: '80px',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      boxSizing: 'border-box'
    },
    parentInfo: {
      padding: '8px 12px',
      backgroundColor: '#f8fafc',
      borderRadius: '6px',
      fontSize: '14px',
      border: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      marginTop: '24px',
      justifyContent: 'flex-end'
    },
    cancelButton: {
      padding: '10px 20px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      backgroundColor: '#ffffff',
      color: '#374151',
      cursor: 'pointer'
    },
    saveButton: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '6px',
      backgroundColor: '#059669',
      color: '#ffffff',
      cursor: 'pointer'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#64748b'
    },
    searchBox: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      marginBottom: '16px',
      boxSizing: 'border-box'
    },
    filterButtons: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    filterButton: {
      padding: '6px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '12px',
      cursor: 'pointer',
      backgroundColor: '#ffffff',
      transition: 'all 0.2s ease-in-out'
    },
    filterButtonActive: {
      backgroundColor: '#059669',
      color: '#ffffff',
      borderColor: '#059669'
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');

  useEffect(() => {
    fetchCategories();
  }, [establishmentId]);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get(
        `create_drinks_establishment/establishment/${establishmentId}/custom-categories`
      );
      setCategories(response.data);
      setHierarchicalCategories(buildHierarchy(response.data));
      if (onCategoriesChange) {
        onCategoriesChange(response.data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories', error);
    }
  };

  const buildHierarchy = (cats) => {
    const categoryMap = {};
    const rootCategories = [];

    // Créer un map de toutes les catégories
    cats.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });

    // Construire la hiérarchie
    cats.forEach(cat => {
      if (cat.parent_id && categoryMap[cat.parent_id]) {
        categoryMap[cat.parent_id].children.push(categoryMap[cat.id]);
      } else {
        rootCategories.push(categoryMap[cat.id]);
      }
    });

    return rootCategories;
  };

  const filterCategories = (cats, search, level) => {
    return cats.filter(cat => {
      const matchesSearch = !search || 
        cat.name.toLowerCase().includes(search.toLowerCase()) ||
        (cat.description && cat.description.toLowerCase().includes(search.toLowerCase()));
      
      const matchesLevel = level === 'all' || 
        (level === 'root' && !cat.parent_id) ||
        (level === 'sub' && cat.parent_id);

      return matchesSearch && matchesLevel;
    });
  };

  const toggleExpand = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const openAddModal = (parentId = null) => {
    setSelectedParentId(parentId);
    setNewCategory({
      name: '',
      description: '',
      parent_id: parentId
    });
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setSelectedParentId(null);
    setNewCategory({
      name: '',
      description: '',
      parent_id: null
    });
  };

  const handleCreateCategory = async () => {
    try {
      const categoryData = {
        ...newCategory,
        parent_id: selectedParentId
      };

      const endpoint = selectedParentId 
        ? `create_drinks_establishment/establishment/${establishmentId}/custom-categories/${selectedParentId}/subcategories`
        : `create_drinks_establishment/establishment/${establishmentId}/custom-categories`;

      const response = await axiosInstance.post(endpoint, categoryData);
      
      await fetchCategories();
      closeAddModal();
      alert('Catégorie créée avec succès !');
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie', error);
      alert('Erreur lors de la création de la catégorie: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdateCategory = async () => {
    try {
      await axiosInstance.put(
        `create_drinks_establishment/establishment/${establishmentId}/custom-categories/${editingCategory.id}`,
        editingCategory
      );
      
      await fetchCategories();
      setEditingCategory(null);
      alert('Catégorie modifiée avec succès !');
    } catch (error) {
      console.error('Erreur lors de la modification de la catégorie', error);
      alert('Erreur lors de la modification de la catégorie');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    // Vérifier s'il y a des sous-catégories
    const hasChildren = categories.some(cat => cat.parent_id === categoryId);
    
    let confirmMessage = 'Êtes-vous sûr de vouloir supprimer cette catégorie ?';
    if (hasChildren) {
      confirmMessage += '\n\nAttention: Cette catégorie contient des sous-catégories qui seront également supprimées.';
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axiosInstance.delete(
        `create_drinks_establishment/establishment/${establishmentId}/custom-categories/${categoryId}`
      );
      
      await fetchCategories();
      alert('Catégorie supprimée avec succès !');
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie', error);
      alert('Erreur lors de la suppression de la catégorie');
    }
  };

  const getParentOptions = () => {
    return categories.filter(cat => !cat.parent_id); // Seulement les catégories racines
  };

  const getCategoryLevel = (category) => {
    if (!category.parent_id) return 0;
    let level = 1;
    let parent = categories.find(cat => cat.id === category.parent_id);
    while (parent && parent.parent_id) {
      level++;
      parent = categories.find(cat => cat.id === parent.parent_id);
    }
    return level;
  };

  const getCategoryPath = (category) => {
    const path = [category.name];
    let parent = categories.find(cat => cat.id === category.parent_id);
    while (parent) {
      path.unshift(parent.name);
      parent = categories.find(cat => cat.id === parent.parent_id);
    }
    return path.join(' > ');
  };

  const renderCategoryItem = (category, isChild = false) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const level = getCategoryLevel(category);

    return (
      <div key={category.id}>
        <div 
          style={{
            ...styles.categoryItem,
            ...(isChild ? styles.categoryItemChild : {})
          }}
        >
          <div style={styles.categoryInfo}>
            {hasChildren && (
              <button
                onClick={() => toggleExpand(category.id)}
                style={styles.expandButton}
                title={isExpanded ? 'Réduire' : 'Développer'}
              >
                {isExpanded ? '−' : '+'}
              </button>
            )}
            
            <div style={styles.categoryDetails}>
              <h4 style={styles.categoryName}>{category.name}</h4>
              {category.description && (
                <p style={styles.categoryDescription}>{category.description}</p>
              )}
              <div style={styles.categoryMeta}>
                Niveau {level + 1} • {getCategoryPath(category)}
                {hasChildren && ` • ${category.children.length} sous-catégorie${category.children.length > 1 ? 's' : ''}`}
              </div>
            </div>
          </div>
          
          <div style={styles.categoryActions}>
            <button
              onClick={() => openAddModal(category.id)}
              style={{...styles.actionButton, ...styles.addSubButton}}
              title="Ajouter une sous-catégorie"
            >
              + Sous
            </button>
            
            <button
              onClick={() => setEditingCategory(category)}
              style={{...styles.actionButton, ...styles.editButton}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e2e8f0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f1f5f9'}
            >
              ✏️ Modifier
            </button>
            
            <button
              onClick={() => handleDeleteCategory(category.id)}
              style={{...styles.actionButton, ...styles.deleteButton}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#fee2e2'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#fef2f2'}
            >
              🗑️ Supprimer
            </button>
          </div>
        </div>

        {/* Sous-catégories */}
        {hasChildren && isExpanded && (
          <div style={{ marginLeft: '20px', marginTop: '8px' }}>
            {category.children.map(child => renderCategoryItem(child, true))}
          </div>
        )}
      </div>
    );
  };

  const filteredCategories = filterCategories(hierarchicalCategories, searchTerm, filterLevel);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>🗂️ Gestion des Catégories</h3>
        <button
          onClick={() => openAddModal()}
          style={styles.addButton}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
        >
          + Ajouter une catégorie
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <input
        type="text"
        placeholder="Rechercher une catégorie..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={styles.searchBox}
      />

     
      {/* Liste des catégories */}
      <div style={styles.categoryList}>
        {filteredCategories.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              {searchTerm ? 'Aucune catégorie trouvée' : 'Aucune catégorie créée'}
            </div>
            <div style={{ fontSize: '14px' }}>
              {searchTerm ? 'Essayez un autre terme de recherche' : 'Créez vos premières catégories pour organiser vos produits'}
            </div>
          </div>
        ) : (
          filteredCategories.map(category => renderCategoryItem(category))
        )}
      </div>

      {/* Modal d'ajout */}
      {isAddModalOpen && (
        <div style={styles.modal} onClick={closeAddModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {selectedParentId ? 'Créer une sous-catégorie' : 'Créer une nouvelle catégorie'}
            </h3>
            
            {selectedParentId && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Catégorie parent</label>
                <div style={styles.parentInfo}>
                  {categories.find(cat => cat.id === selectedParentId)?.name}
                </div>
              </div>
            )}
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nom de la catégorie *</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                style={styles.input}
                placeholder="Ex: Plats principaux"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Description (optionnel)</label>
              <textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                style={styles.textarea}
                placeholder="Description de la catégorie..."
              />
            </div>

            <div style={styles.modalActions}>
              <button
                onClick={closeAddModal}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleCreateCategory}
                style={styles.saveButton}
                disabled={!newCategory.name.trim()}
              >
                {selectedParentId ? 'Créer la sous-catégorie' : 'Créer la catégorie'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification */}
      {editingCategory && (
        <div style={styles.modal} onClick={() => setEditingCategory(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Modifier la catégorie</h3>
            
            {editingCategory.parent_id && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Catégorie parent</label>
                <select
                  value={editingCategory.parent_id || ''}
                  onChange={(e) => setEditingCategory({
                    ...editingCategory, 
                    parent_id: e.target.value || null
                  })}
                  style={styles.select}
                >
                  <option value="">Aucun parent (catégorie principale)</option>
                  {getParentOptions().map(parent => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nom de la catégorie *</label>
              <input
                type="text"
                value={editingCategory.name}
                onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                value={editingCategory.description || ''}
                onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                style={styles.textarea}
              />
            </div>

            <div style={styles.modalActions}>
              <button
                onClick={() => setEditingCategory(null)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateCategory}
                style={styles.saveButton}
                disabled={!editingCategory.name.trim()}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;